import { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { openAuthedFile } from "@/lib/openFile";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, FileSearch, Gift, Loader2 } from "lucide-react";

const pct = (v) => `${Number(v).toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`;
const qty = (v) => Number(v).toLocaleString("de-DE", { maximumFractionDigits: 4 });

function Lines({ label, lines, testId }) {
  if (!lines?.length) return null;
  return (
    <div data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      {lines.map((l, i) => <div key={i} className="text-sm text-slate-700">{l}</div>)}
    </div>
  );
}

function PositionTable({ rows }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md">
      <table className="w-full text-sm" data-testid="vorschau-positionen">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
          <th className="px-2 py-1.5">Pos.</th><th className="px-2 py-1.5">Beschreibung</th><th className="px-2 py-1.5 text-right">Menge</th>
          <th className="px-2 py-1.5 text-right">Einzelpreis</th><th className="px-2 py-1.5 text-right">Rabatt</th><th className="px-2 py-1.5 text-right">Gesamt</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 align-top" data-testid={`vorschau-pos-${i}`}>
              <td className="px-2 py-1.5 text-slate-500">{i + 1}</td>
              <td className="px-2 py-1.5"><div className="font-medium text-slate-900">{r.name}</div>{r.text && <div className="text-xs text-slate-500 whitespace-pre-line">{r.text}</div>}</td>
              <td className="px-2 py-1.5 text-right font-mono">{qty(r.menge)}</td>
              <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">{fmtEUR(r.einzelpreis_brutto)}</td>
              <td className="px-2 py-1.5 text-right font-mono" data-testid={`vorschau-pos-rabatt-${i}`}>{r.rabatt_prozent ? pct(r.rabatt_prozent) : "–"}</td>
              <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">{fmtEUR(r.gesamt_brutto)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Summen({ s, skonto }) {
  const Row = ({ l, v, strong, testId }) => (
    <div className={`flex justify-between gap-6 ${strong ? "font-semibold text-slate-900 border-t border-slate-200 pt-1" : "text-slate-600"}`} data-testid={testId}><span>{l}</span><span className="font-mono">{v}</span></div>
  );
  return (
    <div className="ml-auto w-full sm:w-80 text-sm space-y-0.5" data-testid="vorschau-summen">
      {s.positionsrabatt_betrag > 0 && <Row l="davon Positionsrabatte" v={`−${fmtEUR(s.positionsrabatt_betrag)}`} testId="vorschau-positionsrabatt" />}
      {s.gesamtrabatt_betrag > 0 && <Row l={s.gesamtrabatt_text} v={`−${fmtEUR(s.gesamtrabatt_betrag)}`} testId="vorschau-gesamtrabatt" />}
      <Row l="Netto" v={fmtEUR(s.netto)} testId="vorschau-netto" />
      {s.steuer.map((st) => <Row key={st.satz} l={`MwSt ${st.satz.toLocaleString("de-DE")} %`} v={fmtEUR(st.mwst)} testId={`vorschau-mwst-${st.satz}`} />)}
      <Row l="Brutto" v={fmtEUR(s.brutto)} strong testId="vorschau-brutto" />
      {skonto && <Row l={`Zahlbetrag bei ${pct(skonto.prozent)} Skonto (${skonto.tage} Tage)`} v={fmtEUR(skonto.zahlbetrag)} testId="vorschau-zahlbetrag" />}
    </div>
  );
}

// Vorschau des sevDesk-Dokuments – aus genau dem Payload, der an sevDesk gesendet wird
export function DokumentVorschauInhalt({ orderId, typ }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    setD(null); setErr("");
    api.get(`/auftraege/${orderId}/${typ}-vorschau`).then((r) => alive && setD(r.data)).catch((e) => alive && setErr(e?.response?.data?.detail || "Vorschau konnte nicht geladen werden."));
    return () => { alive = false; };
  }, [orderId, typ]);
  const pdf = async () => {
    setPdfBusy(true);
    try { await openAuthedFile(`/auftraege/${orderId}/vorschau-pdf?typ=${typ}`, `Vorschau_${typ}.pdf`); }
    catch { toast.error("PDF-Vorschau konnte nicht geöffnet werden."); }
    finally { setPdfBusy(false); }
  };
  if (err) return <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-sm" role="alert" data-testid="vorschau-error">{err}</div>;
  if (!d) return <div className="py-8 flex justify-center" data-testid="vorschau-loading"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const e = d.empfaenger;
  return (
    <div className="space-y-4 text-sm" data-testid="dokument-vorschau">
      {d.kostenlos && (
        <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 flex items-start gap-2" data-testid="vorschau-kostenlos-hinweis">
          <Gift className="w-4 h-4 mt-0.5 shrink-0" /> <span><b>{d.hinweis}</b> – Kostenlose Lieferung: keine Zahlung, keine Rechnung.</span>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div data-testid="vorschau-empfaenger">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Empfänger</div>
          {e.adresse.map((l, i) => <div key={i} className={i === 0 ? "font-medium text-slate-900" : "text-slate-700"}>{l}</div>)}
          {e.email && <div className="text-xs text-slate-500 mt-0.5">{e.email}</div>}
        </div>
        <div className="space-y-1">
          <div><span className="text-slate-500">Dokument:</span> <b data-testid="vorschau-typ">{d.dokument}</b></div>
          <div><span className="text-slate-500">Nummer:</span> {d.nummer}</div>
          <div><span className="text-slate-500">Titel:</span> {d.titel}</div>
          <div><span className="text-slate-500">Briefpapier:</span> <span data-testid="vorschau-briefpapier">{d.briefpapier}</span></div>
        </div>
      </div>
      <Lines label="Kopftext" lines={d.kopftext} testId="vorschau-kopftext" />
      <PositionTable rows={d.positionen} />
      <Summen s={d.summen} skonto={d.skonto} />
      {!d.summen.payload_stimmt && (
        <p className="text-xs text-amber-800 flex items-start gap-1.5" data-testid="vorschau-rundung">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> sevDesk rundet Bruttopreise je Position – Abweichung von {fmtEUR(Math.abs(d.summen.payload_brutto - d.summen.brutto))} möglich. Das Dokument wird nach dem Anlegen centgenau geprüft.
        </p>
      )}
      <Lines label="Fußtext" lines={d.fusstext} testId="vorschau-fusstext" />
      <Lines label="Zahlungsbedingungen" lines={d.zahlungsbedingungen} testId="vorschau-zahlungsbedingungen" />
      <div className="flex items-center gap-2 pt-1">
        <Button type="button" size="sm" variant="outline" onClick={pdf} disabled={pdfBusy} data-testid="vorschau-pdf">
          {pdfBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSearch className="w-4 h-4 mr-1" />} PDF-Vorschau
        </Button>
        <span className="text-xs text-slate-500">{d.kostenlos ? "Vorschau des CRM-Angebots-PDF (Nummer wird bei der Freigabe vergeben)." : "Vorschau – das endgültige Layout kommt aus sevDesk."}</span>
      </div>
    </div>
  );
}

export function DokumentVorschauDialog({ open, onOpenChange, orderId, typ, onConfirm, busy, kostenlos }) {
  const title = kostenlos ? "Vorschau: CRM-Angebot (0,00 €)" : `Vorschau: ${typ === "angebot" ? "sevDesk-Angebot" : "sevDesk-Rechnung"}`;
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`vorschau-dialog-${typ}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription data-testid="vorschau-dialog-beschreibung">{kostenlos
            ? "Wird im CRM erstellt (sevDesk unterstützt keine 0-€-Belege). Bitte prüfen und dann „Jetzt anlegen“."
            : "So wird das Dokument in sevDesk angelegt. Bitte prüfen und dann „Jetzt anlegen“."}</DialogDescription>
        </DialogHeader>
        {open && <DokumentVorschauInhalt orderId={orderId} typ={typ} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="vorschau-cancel">Abbrechen</Button>
          <Button onClick={onConfirm} disabled={busy} data-testid="vorschau-confirm">{busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird angelegt…</> : "Jetzt anlegen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
